import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type {
  CreateEmployeeProfileBlockFieldInput,
  EmployeeProfileBlockField,
  EmployeeProfileFieldType,
} from '@hygge/shared';
import { FIELD_TYPE_META, getFieldTypeMeta } from './field-type-meta';

interface FieldConfigDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  field?: EmployeeProfileBlockField | null;
  isSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: CreateEmployeeProfileBlockFieldInput) => void;
}

const NAME_MAX = 100;
const DESCRIPTION_MAX = 500;

interface FormState {
  name: string;
  description: string;
  type: EmployeeProfileFieldType;
  required: boolean;
  options: string[];
}

function initialState(field: EmployeeProfileBlockField | null | undefined): FormState {
  return {
    name: field?.name ?? '',
    description: field?.description ?? '',
    type: field?.type ?? 'SHORT_TEXT',
    required: field?.required ?? false,
    options: field?.options ?? (field?.type === 'DROPDOWN' ? ['', ''] : []),
  };
}

export function FieldConfigDialog({
  open,
  mode,
  field,
  isSubmitting = false,
  onOpenChange,
  onSubmit,
}: FieldConfigDialogProps) {
  const [form, setForm] = useState<FormState>(() => initialState(field));

  useEffect(() => {
    if (open) setForm(initialState(field));
  }, [open, field]);

  const trimmedName = form.name.trim();
  const trimmedOptions = useMemo(
    () => form.options.map((o) => o.trim()),
    [form.options],
  );

  const dropdownIssue = useMemo(() => {
    if (form.type !== 'DROPDOWN') return null;
    if (trimmedOptions.length < 2) return 'Add at least 2 options';
    if (trimmedOptions.some((o) => o.length === 0)) return 'Options cannot be empty';
    const lower = trimmedOptions.map((o) => o.toLowerCase());
    if (new Set(lower).size !== lower.length) return 'Options must be unique';
    return null;
  }, [form.type, trimmedOptions]);

  // Every field the submit needs, in label order — drives both the `*` markers and the
  // "Required: …" hint on the disabled button, so a blocked submit says what is missing.
  // Options are deliberately absent: a dropdown always carries at least two rows, so what
  // fails there is their content, not their presence, and `dropdownIssue` names that right
  // above the button — listing them as missing would contradict it.
  const missingFields: string[] = [];
  if (trimmedName.length === 0) missingFields.push('Field Name');

  const canSubmit =
    !isSubmitting &&
    missingFields.length === 0 &&
    dropdownIssue === null &&
    trimmedName.length <= NAME_MAX &&
    form.description.length <= DESCRIPTION_MAX;

  const handleTypeChange = (next: EmployeeProfileFieldType) => {
    setForm((prev) => ({
      ...prev,
      type: next,
      options:
        next === 'DROPDOWN'
          ? prev.options.length >= 2
            ? prev.options
            : ['', '']
          : [],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      name: trimmedName,
      description: form.description.trim() || null,
      type: form.type,
      required: form.required,
      options: form.type === 'DROPDOWN' ? trimmedOptions : null,
    });
  };

  const addOption = () =>
    setForm((prev) => ({ ...prev, options: [...prev.options, ''] }));

  const updateOption = (index: number, value: string) =>
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => (i === index ? value : o)),
    }));

  const removeOption = (index: number) =>
    setForm((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{mode === 'create' ? 'Add Field' : 'Edit Field'}</DialogTitle>
            <DialogDescription>
              {mode === 'create'
                ? 'Define a new piece of information stored on every employee profile.'
                : 'Update this field — type changes are blocked once values exist.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="field-name">
                Field Name{' '}
                <span className="text-destructive" aria-label="Required">
                  *
                </span>
              </Label>
              <Input
                id="field-name"
                autoFocus
                // State, not native validation: `required` would let the browser pre-empt the
                // form's own messaging (see HW-764).
                aria-required="true"
                value={form.name}
                maxLength={NAME_MAX}
                placeholder="e.g. Tax ID"
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
              <p className="text-[11px] text-muted-foreground">
                {trimmedName.length}/{NAME_MAX}
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="field-description">Description / Hint (optional)</Label>
              <Textarea
                id="field-description"
                rows={2}
                value={form.description}
                maxLength={DESCRIPTION_MAX}
                placeholder="Shows as a tooltip next to the field."
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="grid gap-2">
              <Label>Field Type</Label>
              <Select value={form.type} onValueChange={(v) => handleTypeChange(v as EmployeeProfileFieldType)}>
                <SelectTrigger>
                  <SelectValue>{getFieldTypeMeta(form.type).label}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPE_META.map((meta) => (
                    <SelectItem key={meta.value} value={meta.value} textValue={meta.label}>
                      <div className="flex flex-col">
                        <span>{meta.label}</span>
                        <span className="text-[11px] text-muted-foreground">{meta.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
              <div>
                <Label htmlFor="field-required" className="text-sm">Required</Label>
                <p className="text-[11px] text-muted-foreground">Employees must have a value.</p>
              </div>
              <Switch
                id="field-required"
                checked={form.required}
                onCheckedChange={(checked) => setForm((prev) => ({ ...prev, required: checked }))}
              />
            </div>

            {form.type === 'DROPDOWN' && (
              <div className="grid gap-2">
                <Label id="field-options-label">
                  Options{' '}
                  <span className="text-destructive" aria-label="Required">
                    *
                  </span>
                </Label>
                {/* The option inputs are named by their placeholder alone and the Label has no
                    control to point `htmlFor` at, so the marker would otherwise be visual only.
                    A group name is not part of its children's names — this announces the
                    requirement on entering the group, not on each row. */}
                <div className="space-y-1.5" role="group" aria-labelledby="field-options-label">
                  {form.options.map((opt, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={opt}
                        placeholder={`Option ${index + 1}`}
                        maxLength={100}
                        onChange={(e) => updateOption(index, e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                        disabled={form.options.length <= 2}
                        onClick={() => removeOption(index)}
                        aria-label="Remove option"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="self-start gap-1.5"
                  onClick={addOption}
                  disabled={form.options.length >= 50}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add option
                </Button>
                {/* The rule stands whether or not it is currently broken — a fresh
                    dropdown starts on two empty options, so a rule shown only while
                    valid would never be read. */}
                <p className="text-[11px] text-muted-foreground">
                  At least 2 unique, non-empty options.
                </p>
                {dropdownIssue && (
                  <p className="text-[11px] text-destructive">{dropdownIssue}</p>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {!isSubmitting && missingFields.length > 0
                ? `Required: ${missingFields.join(', ')}`
                : ''}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {mode === 'create' ? 'Add Field' : 'Save'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
